import * as Sentry from '@sentry/browser';
import { find, compact } from 'lodash';
import { observable, action, runInAction, computed, makeObservable } from 'mobx';
import { AssociableEntitiesDto, AssociableRoleDto } from '@/store/associable-entities.dto';

import epaxios from '@/utils/epaxios';

import RootStore from './RootStore';

export default class AssociableEntitiesStore {
  rootStore: typeof RootStore;
  @observable stations: Station[] = [];
  @observable isLoading: boolean;
  @observable isReady: boolean;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action get = async () => {
    try {
      this.isLoading = true;
      const { data } = (await epaxios.get('msp/user/getAssociableEntities')) as { data: AssociableEntitiesDto };

      runInAction(() => {
        this.stations = data.stations.map(station => new Station(station));
        this.isReady = true;
      });
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Associable Entities: ${e}`));
    } finally {
      this.isLoading = false;
    }
  };

  @action getStationById = (id: string) => {
    return find(this.stations, { id });
  };

  @action getRolesByStationId = (id: string) => {
    const foundStation = find(this.stations, { id });
    return foundStation?.roles || [];
  };
}

class Station {
  @observable id: string;
  @observable name: string;
  @observable siteName: string;
  @observable customerName: string;
  @observable roles: Role[];

  constructor(station) {
    makeObservable(this);
    this.id = station.id;
    this.name = station.name;
    this.siteName = station.siteName;
    this.customerName = station.customerName;
    this.roles = station.roles.map((role: AssociableRoleDto) => new Role(role));
  }

  @computed get fullName() {
    const compactedNames = compact([this.customerName, this.siteName, this.name]);
    const name = compactedNames.join(' | ');
    return name;
  }
}

export class Role {
  @observable id: string;
  @observable name: string;
  @observable isInternal: boolean;

  constructor(role: AssociableRoleDto) {
    makeObservable(this);
    this.id = role.id;
    this.name = role.name;
    this.isInternal = role.isInternal;
  }
}
